<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useDialogA11y } from '../../composables/useDialogA11y';
import ImageErrorFallback from './ImageErrorFallback.vue';
import {
  buildMediaTabs,
  groupMediaByType,
  isVideoMediaType,
  MEDIA_TYPES,
} from './galleryUtils';

const props = defineProps({
  open: { type: Boolean, default: false },
  items: { type: Array, default: () => [] },
  tabs: { type: Array, default: () => [] },
  initialIndex: { type: Number, default: 0 },
  initialTab: { type: String, default: MEDIA_TYPES.PHOTOS },
  title: { type: String, default: 'Galeria de fotos' },
  disableRightClick: { type: Boolean, default: true },
});

const emit = defineEmits(['close', 'update:open', 'change']);

const activeIndex = ref(props.initialIndex);
const activeTab = ref(props.initialTab);
const touchStartX = ref(null);
const imageErrors = ref({});
const zoomed = ref(false);
const zoomScale = ref(1);
const zoomOrigin = ref('50% 50%');
const pan = reactive({ x: 0, y: 0 });
const panStart = reactive({ x: 0, y: 0, panX: 0, panY: 0, active: false });

const mediaGroups = computed(() => groupMediaByType(props.items));
const availableTabs = computed(() => {
  const tabs = props.tabs.length
    ? props.tabs
    : buildMediaTabs(mediaGroups.value);
  return tabs.filter(tab => mediaGroups.value[tab.id]?.length);
});
const activeItems = computed(() => mediaGroups.value[activeTab.value] || []);
const activeItem = computed(() => activeItems.value[activeIndex.value]);
const canNavigate = computed(() => activeItems.value.length > 1);
const titleId = computed(() => 'lais-image-lightbox-title');
const closeLabel = 'Fechar galeria';
const previousLabel = 'Mídia anterior';
const nextLabel = 'Próxima mídia';
const mediaTypesLabel = 'Tipos de mídia';
const tabCounterLabel = tab => `${tab.label} · ${tab.count}`;
const counterText = computed(
  () => `${activeIndex.value + 1} / ${activeItems.value.length}`
);
const thumbLabel = index => `Abrir mídia ${index + 1}`;
const hasImageError = item => Boolean(item?.src && imageErrors.value[item.src]);
const isActiveVideo = computed(() => isVideoMediaType(activeItem.value?.type));
const imageStyle = computed(() =>
  zoomed.value
    ? {
        cursor: panStart.active ? 'grabbing' : 'grab',
        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoomScale.value})`,
        transformOrigin: zoomOrigin.value,
      }
    : {}
);

const resetZoom = () => {
  zoomed.value = false;
  zoomScale.value = 1;
  zoomOrigin.value = '50% 50%';
  pan.x = 0;
  pan.y = 0;
  panStart.active = false;
};

const close = () => {
  emit('update:open', false);
  emit('close');
};

const emitChange = () => {
  emit('change', {
    index: activeIndex.value,
    item: activeItem.value,
    tab: activeTab.value,
  });
};

const goTo = index => {
  const length = activeItems.value.length;
  if (!length) return;

  const nextIndex = Math.max(0, Math.min(index, length - 1));
  activeIndex.value = nextIndex;
  resetZoom();
  emitChange();
};

const selectTab = tabId => {
  if (!mediaGroups.value[tabId]?.length) return;
  activeTab.value = tabId;
  activeIndex.value = 0;
  resetZoom();
  emitChange();
};

const handleKeydown = event => {
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    goTo(activeIndex.value - 1);
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    goTo(activeIndex.value + 1);
  }
};

const handleTouchStart = event => {
  touchStartX.value = event.touches?.[0]?.clientX ?? null;
};

const handleTouchEnd = event => {
  if (touchStartX.value == null || zoomed.value) return;
  const endX = event.changedTouches?.[0]?.clientX ?? touchStartX.value;
  const delta = endX - touchStartX.value;
  touchStartX.value = null;
  if (Math.abs(delta) < 36) return;
  goTo(delta > 0 ? activeIndex.value - 1 : activeIndex.value + 1);
};

const handleContextMenu = event => {
  if (props.disableRightClick) {
    event.preventDefault();
  }
};

const markImageError = item => {
  if (!item?.src) return;
  imageErrors.value = { ...imageErrors.value, [item.src]: true };
};

const toggleZoom = event => {
  if (isActiveVideo.value || !activeItem.value?.src) return;
  if (zoomed.value) {
    resetZoom();
    return;
  }

  const rect = event.currentTarget.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 100;
  const y = ((event.clientY - rect.top) / rect.height) * 100;
  zoomOrigin.value = `${x}% ${y}%`;
  zoomScale.value = 2;
  zoomed.value = true;
};

const startPan = event => {
  if (!zoomed.value || event.button !== 0) return;
  panStart.active = true;
  panStart.x = event.clientX;
  panStart.y = event.clientY;
  panStart.panX = pan.x;
  panStart.panY = pan.y;
};

const movePan = event => {
  if (!panStart.active) return;
  pan.x = panStart.panX + event.clientX - panStart.x;
  pan.y = panStart.panY + event.clientY - panStart.y;
};

const endPan = () => {
  panStart.active = false;
};

const { dialogRef } = useDialogA11y({
  isOpen: computed(() => props.open),
  onClose: close,
});

watch(
  () => props.initialTab,
  value => {
    activeTab.value = mediaGroups.value[value]?.length
      ? value
      : availableTabs.value[0]?.id || MEDIA_TYPES.PHOTOS;
    resetZoom();
  }
);

watch(
  () => props.initialIndex,
  value => {
    activeIndex.value = Math.min(
      Math.max(Number(value) || 0, 0),
      Math.max(activeItems.value.length - 1, 0)
    );
    resetZoom();
  }
);

watch(
  () => availableTabs.value.map(tab => tab.id).join('|'),
  () => {
    if (!mediaGroups.value[activeTab.value]?.length) {
      activeTab.value = availableTabs.value[0]?.id || MEDIA_TYPES.PHOTOS;
      activeIndex.value = 0;
      resetZoom();
    }
  },
  { immediate: true }
);

watch(
  () => activeItems.value.length,
  length => {
    if (activeIndex.value >= length) {
      activeIndex.value = Math.max(length - 1, 0);
      resetZoom();
    }
  }
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-3"
      @click.self="close"
    >
      <section
        ref="dialogRef"
        class="relative flex h-full max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-lais bg-lais-bg-card shadow-lais-pop outline-none"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
        @keydown="handleKeydown"
      >
        <header
          class="flex min-h-14 items-center justify-between gap-3 border-b border-lais-border px-4"
        >
          <div class="min-w-0">
            <h2
              :id="titleId"
              class="truncate text-lais-heading-sm font-semibold text-lais-text"
            >
              {{ title }}
            </h2>
            <p class="text-lais-label-sm text-lais-text-helper">
              {{ counterText }}
            </p>
          </div>
          <button
            type="button"
            class="inline-flex size-9 items-center justify-center rounded-full text-lais-text-muted transition hover:bg-lais-bg-muted hover:text-lais-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-lais-primary-50"
            :aria-label="closeLabel"
            @click="close"
          >
            <span class="i-lucide-x size-5" aria-hidden="true" />
          </button>
        </header>

        <nav
          v-if="availableTabs.length > 1"
          class="flex gap-2 overflow-x-auto border-b border-lais-border bg-lais-bg-card px-4 py-3"
          :aria-label="mediaTypesLabel"
        >
          <button
            v-for="tab in availableTabs"
            :key="tab.id"
            type="button"
            class="h-9 shrink-0 rounded-full px-3 text-[12px] font-semibold transition"
            :class="
              activeTab === tab.id
                ? 'bg-lais-primary text-white'
                : 'bg-lais-bg-muted text-lais-text-muted hover:text-lais-text'
            "
            :aria-current="activeTab === tab.id"
            @click="selectTab(tab.id)"
          >
            {{ tabCounterLabel(tab) }}
          </button>
        </nav>

        <div
          class="relative flex min-h-0 flex-1 items-center justify-center bg-black"
          @touchstart.passive="handleTouchStart"
          @touchend.passive="handleTouchEnd"
          @mousemove="movePan"
          @mouseup="endPan"
          @mouseleave="endPan"
        >
          <button
            v-if="canNavigate"
            type="button"
            class="absolute left-3 z-10 inline-flex size-10 items-center justify-center rounded-full bg-white/90 text-lais-text shadow-lais-card transition hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
            :aria-label="previousLabel"
            @click="goTo(activeIndex - 1)"
          >
            <span class="i-lucide-chevron-left size-5" aria-hidden="true" />
          </button>

          <iframe
            v-if="isActiveVideo && activeItem?.src"
            :src="activeItem.src"
            :title="activeItem.alt"
            class="h-full min-h-[320px] w-full border-0"
            allowfullscreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          />
          <ImageErrorFallback
            v-else-if="!activeItem?.src || hasImageError(activeItem)"
            fullscreen
          />
          <img
            v-else
            :src="activeItem.src"
            :alt="activeItem.alt"
            class="max-h-full max-w-full select-none object-contain transition-transform"
            :class="zoomed ? 'cursor-grab' : 'cursor-zoom-in'"
            :style="imageStyle"
            :draggable="false"
            @contextmenu="handleContextMenu"
            @dblclick="toggleZoom"
            @mousedown.stop="startPan"
            @error="markImageError(activeItem)"
          />

          <button
            v-if="canNavigate"
            type="button"
            class="absolute right-3 z-10 inline-flex size-10 items-center justify-center rounded-full bg-white/90 text-lais-text shadow-lais-card transition hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
            :aria-label="nextLabel"
            @click="goTo(activeIndex + 1)"
          >
            <span class="i-lucide-chevron-right size-5" aria-hidden="true" />
          </button>
        </div>

        <footer
          v-if="activeItems.length"
          class="flex gap-2 overflow-x-auto border-t border-lais-border bg-lais-bg-card p-3"
        >
          <button
            v-for="(item, index) in activeItems"
            :key="item.id"
            type="button"
            class="h-16 w-24 shrink-0 overflow-hidden rounded-lais border transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-lais-primary-50"
            :class="
              index === activeIndex
                ? 'border-lais-primary'
                : 'border-lais-border'
            "
            :aria-label="thumbLabel(index)"
            :aria-current="index === activeIndex"
            @click="goTo(index)"
          >
            <img
              v-if="
                item.src && !hasImageError(item) && !isVideoMediaType(item.type)
              "
              :src="item.thumbSrc || item.src"
              :alt="item.alt"
              class="size-full object-cover"
              loading="lazy"
              @error="markImageError(item)"
            />
            <span
              v-else-if="isVideoMediaType(item.type)"
              class="flex size-full items-center justify-center bg-black text-white"
            >
              <span
                :class="
                  item.type === MEDIA_TYPES.VIDEO_360
                    ? 'i-lucide-rotate-3d'
                    : 'i-lucide-play'
                "
                class="size-5"
                aria-hidden="true"
              />
            </span>
            <ImageErrorFallback v-else />
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>
